import { jsonToGraphQLQuery } from "json-to-graphql-query";
import { handleQuery } from "../utils/request";
import axiosObj from "./config";

export const DATASET_API_URL = `${process.env.API_URL}/svc-dataset/graphql`;
const MODEL_API_URL = `${process.env.API_URL}/model/graphql`;

const DATASET_ASSOCIATE_MODEL_RETURN_VALUES = {
  id: true,
  name: true,
  description: true,
  tags: true,
  source: true,
  modelType: true,
  categories: true,
  framework: true,
  deviceTypes: true,
  license: true,
  modelSize: true,
  imageSize: true,
  labels: true,
  url: true,
  createdAt: true,
  updatedAt: true
};

const DATASET_ASSOCIATE_JOB_RETURN_VALUES = {
  _id: true,
  name: true,
  status: true,
  taskId: true,
  progress: true,
  frameCount: true,
  datasetId: true,
  createdAt: true,
  assignedTo: {
    _id: true,
    firstName: true,
    lastName: true
  }
};

export const PROJECT_TYPE = {
  id: true,
  name: true,
  createdAt: true,
  updatedAt: true,
};

export const AUTHOR = {
  id: true,
  name: true,
};

export const TAG_ITEMS = {
  key: true,
  value: true
}

export const TAG_RESPONSE = {
  values: [TAG_ITEMS]
}

export const DATASET_DETAIL_TYPE = {
  id: true,
  name: true,
  project: PROJECT_TYPE,
  description: true,
  tags: true,
  mediaType: true,
  source: true,
  author: true,
  createdAt: true,
  updatedAt: true,
  createdBy: AUTHOR,
  updatedBy: AUTHOR,
  info: true,
  version: true,
  status: true,
  brainMethods: true,
  annotationLabels: true,
  augmentations: true,
  dataSplit: {
    original: true,
    training: true,
    validation: true,
    testing: true,
    total: true
  }
};

export const DATASET_DETAIL_TYPE_LIMITED = {
  id: true,
  name: true,
  dataSplit: {
    original: true,
    training: true,
    validation: true,
    testing: true,
    total: true
  }
};

export const DATASET_TYPE = {
  id: true,
  name: true,
  project: PROJECT_TYPE,
  description: true,
  tags: true,
  mediaType: true,
  source: true,
  author: true,
  createdAt: true,
  updatedAt: true,
  createdBy: AUTHOR,
  updatedBy: AUTHOR,
  info: true,
  version: true,
  status: true,
  demoData: true,
  annotationLabels: true
};

export const DATASET_STATS = {
  totalImages: true,
  unAnnotatedImages: true,
  annotatedImages: true,
  totalAnnotations: true,
  classesAcrossAnnotations: {
    count: true,
    class: true,
    share: true,
    representation: true
  },
  classesAcrossImages: {
    count: true,
    class: true,
    share: true,
    representation: true
  },
  totalClasses: true,
  avgImagesPerClass: true,
  avgAnnotationsPerImage: true,
  annotatedPercentage: true,
  annotationDistributionGraph: {
    _id: true,
    totalAnnotations: true,
  }
};

export const DATASET_VALIDATE = {
  blockers: true,
  warnings: true,
};

export const DATASET_VIEWING_LOCK = {
  locked: true,
  lockedBy: {
    id: true,
    name: true
  },
  locked: true,
  message: true
};


export function getDatasetTags(args) {
  const query = {
    query: {
      datasetTags: {
        __args: args
      }
    }
  }
  return handleQuery(query, DATASET_API_URL)
}

export function updateDatasetTags(args) {
  const query = {
    mutation: {
      bulkUpdateDatasetTags: {
        __args: args,
      },
    },
  }
  return handleQuery(query, DATASET_API_URL)
}

export function getDatasets(params) {
  const query = {
    query: {
      datasets: {
        ...DATASET_TYPE,
        __args: params
      },
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function getDatasetDetail({ id }) {
  const query = {
    query: {
      dataset: {
        __args: {
          id,
        },
        ...DATASET_DETAIL_TYPE,
      },
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function getDatasetDetailPartialInfo({ id }) {
  const query = {
    query: {
      dataset: {
        __args: {
          id,
        },
        ...DATASET_DETAIL_TYPE_LIMITED,
      },
    },
  };

  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  return axiosObj({
    method: "POST",
    url: DATASET_API_URL,
    data: {
      variables: {},
      query: gqlQuery,
    },
  });
}

export function getProjects() {
  const query = {
    query: {
      projects: {
        ...PROJECT_TYPE,
      },
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function getTags(args) {
  const query = {
    query: {
      aggregate: {
        __args: args,
        ...TAG_RESPONSE,
      },
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function getAggregate(args) {
  const query = {
    query: {
      aggregates: {
        __args: args
      },
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function getLabels(args) {
  const query = {
    query: {
      getLabels: {
        __args: args,
      },
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function getOtherLabels(args) {
  const query = {
    query: {
      getOtherLabels: {
        __args: args,
      },
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function createDataset(args) {
  const query = {
    mutation: {
      createDataset: {
        __args: args,
        id: true,
        name: true
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function addSamples(args) {
  const query = {
    mutation: {
      addSamples: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function setDataset(args) {
  const query = {
    mutation: {
      setDatasetWithLock: {
        __args: args,
      }
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function unlockFiftyOne() {
  const query = {
    mutation: {
      unlockFiftyOne
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function createProject(args) {
  const query = {
    mutation: {
      createProject: {
        __args: args,
        name: true,
        id: true,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function saveDataset(args) {
  const query = {
    mutation: {
      saveDataset: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function cloneDataset(args) {
  // const query = {
  //   mutation: {
  //     cloneDataset: {
  //       __args: args,
  //       id: true
  //     },
  //   },
  // };
  // const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });
  // return axiosObj({
  //   method: "POST",
  //   url: DATASET_API_URL,
  //   data: {
  //     variables: {},
  //     query: gqlQuery,
  //   },
  // });
  let tags = args.tags.length ? `[${args.tags.map(item => `"${item}"`)}]` : "[]";

  var data = JSON.stringify({
    query: `mutation($form: JSON) {
      cloneDataset (
        datasetId: "${args.datasetId}",
        name: "${args.name}",
        author: "${args.author}",
        source: "${args.source}",
        tags: ${tags},
        projectId: "${args.projectId}",
        form:$form
    )
  }`,
    variables: args.form || { form: "" }
  })

  if (args.training || args.validation || args.testing) {

    data = JSON.stringify({
      query: `mutation($form: JSON) {
        cloneDataset (
          datasetId: "${args.datasetId}",
          name: "${args.name}",
          author: "${args.author}",
          source: "${args.source}",
          tags: ${tags},
          projectId: "${args.projectId}",
          training: "${args.training || ""}",
          validation: "${args.validation || ""}",
          testing: "${args.testing || ""}",
          form:$form
      )
    }`,
      variables: args.form || { form: "" }
    })
  }

  return axiosObj({
    method: "POST",
    url: DATASET_API_URL,
    data: data,
  });
}

export function updateDataset(args) {
  const query = {
    mutation: {
      updateDataset: {
        __args: args,
        id: true,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function loadCvatDataset(args) {
  const query = {
    mutation: {
      loadCvatDataset: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function loadCvatDatasetSelected(args) {
  const query = {
    mutation: {
      loadCvatDatasetSelected: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function mergeAnnotation(args) {
  const query = {
    mutation: {
      mergeAnnotation: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function addVideoSamples(args) {
  const query = {
    mutation: {
      addVideoSamples: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function deleteDataset(args) {
  const query = {
    mutation: {
      deleteDataset: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function deleteDatasets(args) {
  const query = {
    mutation: {
      deleteDatasets: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function setView(args) {
  var data = JSON.stringify({
    query: `query($form: JSON) {
    setView (
        datasetId: "${args.datasetId}",
        filters:$form
    )
  }`,
    variables: args.form
  })

  return axiosObj({
    method: "POST",
    url: DATASET_API_URL,
    data: data,
  });
}

export function filterByTags(args) {
  const query = {
    query: {
      filterByTags: {
        __args: args,
      },
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function updateView(args) {
  const query = {
    query: {
      updateView: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function takeDataset(args) {
  const query = {
    mutation: {
      takeDataset: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function splitDataset(args) {
  const query = {
    mutation: {
      splitDataset: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function getUniquenessScore(args) {
  const query = {
    mutation: {
      uniquenessCalculate: {
        __args: {
          id: args
        },
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function getSimilarityScore(args) {
  const query = {
    mutation: {
      similarityCalculate: {
        __args: {
          id: args
        },
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function deleteSample(args) {
  const query = {
    mutation: {
      deleteSample: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}

export function calculationDataset() {
  const query = {
    query: {
      calculationDataset: {
        ...DATASET_TYPE,
      },
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function cancelCalculation() {
  const query = {
    mutation: {
      cancelCalculation
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function datasetClassList(args) {
  const query = {
    query: {
      datasetClassList: {
        __args: args,
      },
    },
  };
  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  return axiosObj({
    method: 'POST',
    url: DATASET_API_URL,
    data: {
      variables: {},
      query: gqlQuery,
    },
  });
}

export function datasetStats(args) {
  const query = {
    query: {
      datasetStats: {
        __args: args,
        ...DATASET_STATS
      },
    },
  };
  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  return axiosObj({
    method: 'POST',
    url: DATASET_API_URL,
    data: {
      variables: {},
      query: gqlQuery,
    },
  });
}

export function validateDataset(args) {
  const query = {
    query: {
      validateDataset: {
        __args: args,
        ...DATASET_VALIDATE
      },
    },
  };
  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  return axiosObj({
    method: 'POST',
    url: DATASET_API_URL,
    data: {
      variables: {},
      query: gqlQuery,
    },
  });
}

export function importAnnotations(args) {
  const query = {
    mutation: {
      importAnnotations: {
        __args: args,
      },
    },
  };
  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });
  return axiosObj({
    method: 'POST',
    url: DATASET_API_URL,
    data: {
      variables: {},
      query: gqlQuery,
    },
  });
}

export function associatedModels(args) {
  const query = {
    query: {
      associatedModels: {
        __args: args,
        ...DATASET_ASSOCIATE_MODEL_RETURN_VALUES,
      },
    },
  };
  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  return axiosObj({
    method: 'POST',
    url: MODEL_API_URL,
    data: {
      variables: {},
      query: gqlQuery,
    },
  });
}

export function associatedAnnotationJobs(args) {
  const query = {
    query: {
      associatedAnnotationJobs: {
        __args: args,
        count: true,
        data: DATASET_ASSOCIATE_JOB_RETURN_VALUES,
      },
    },
  };
  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  return axiosObj({
    method: 'POST',
    url: DATASET_API_URL,
    data: {
      variables: {},
      query: gqlQuery,
    },
  });
}

export function datasetViewingLock(params) {
  const query = {
    query: {
      datasetViewingLock: {
        ...DATASET_VIEWING_LOCK,
        __args: params
      },
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function datasetProcessingLock(params) {
  const query = {
    query: {
      datasetProcessingLock: {
        ...DATASET_VIEWING_LOCK,
        __args: params
      },
    },
  };

  return handleQuery(query, DATASET_API_URL)
}

export function augmentations() {
  const query = {
    query: {
      augmentations,
    },
  };

  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  return axiosObj({
    method: "POST",
    url: DATASET_API_URL,
    data: {
      variables: {},
      query: gqlQuery,
    },
  });
}

export function augmentDataset(args) {
  const query = {
    mutation: {
      augmentDataset: {
        __args: args,
      },
    },
  };
  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });
  return axiosObj({
    method: "POST",
    url: DATASET_API_URL,
    data: {
      variables: {},
      query: gqlQuery,
    },
  });
}

export function deleteAugmentations(args) {
  const query = {
    mutation: {
      deleteAugmentations: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DATASET_API_URL)
}